/**
 * SetterPerformanceTable Component
 *
 * Sortable table showing setter performance metrics:
 * - Route count
 * - Average rating
 * - Total sends
 *
 * Helps gym owners track setter productivity and quality.
 */

import { useState, useMemo, type CSSProperties } from 'react';

/**
 * Setter performance metrics
 */
interface SetterStats {
  setterId: string;
  setterName: string;
  routeCount: number;
  averageRating: number | null;
  totalSends: number;
}

/**
 * Props for SetterPerformanceTable component
 */
interface SetterPerformanceTableProps {
  /** Array of setter performance stats */
  setters: SetterStats[];
}

/**
 * Sort field options
 */
type SortField = 'setterName' | 'routeCount' | 'averageRating' | 'totalSends';

/**
 * Sort direction
 */
type SortDirection = 'asc' | 'desc';

/**
 * SetterPerformanceTable displays setter metrics in a sortable table
 *
 * @example
 * ```tsx
 * <SetterPerformanceTable setters={analytics.setterPerformance} />
 * ```
 */
export const SetterPerformanceTable = ({ setters }: SetterPerformanceTableProps) => {
  const [sortField, setSortField] = useState<SortField>('routeCount');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Sort setters based on current sort field and direction
  const sortedSetters = useMemo(() => {
    return [...setters].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'setterName':
          comparison = a.setterName.localeCompare(b.setterName);
          break;
        case 'routeCount':
          comparison = a.routeCount - b.routeCount;
          break;
        case 'averageRating': {
          // Treat null ratings as lowest
          const aRating = a.averageRating ?? -1;
          const bRating = b.averageRating ?? -1;
          comparison = aRating - bRating;
          break;
        }
        case 'totalSends':
          comparison = a.totalSends - b.totalSends;
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [setters, sortField, sortDirection]);

  /**
   * Handle column header click for sorting
   */
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default direction
      setSortField(field);
      setSortDirection(field === 'setterName' ? 'asc' : 'desc');
    }
  };

  /**
   * Get sort indicator for column header
   */
  const getSortIndicator = (field: SortField) => {
    if (sortField !== field) {
      return <span style={sortIndicatorInactiveStyle}>&#8597;</span>;
    }
    return (
      <span style={sortIndicatorActiveStyle}>
        {sortDirection === 'asc' ? '&#9650;' : '&#9660;'}
      </span>
    );
  };

  if (setters.length === 0) {
    return (
      <div style={containerStyle}>
        <h3 style={titleStyle}>Setter Performance</h3>
        <div style={emptyStateStyle}>
          <p style={emptyTextStyle}>No setters yet.</p>
          <p style={emptySubtextStyle}>Invite setters to start tracking their performance.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <h3 style={titleStyle}>Setter Performance</h3>
      <p style={subtitleStyle}>Track setter productivity and route quality</p>

      <div style={tableContainerStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th
                style={{ ...thStyle, ...thClickableStyle, textAlign: 'left' }}
                onClick={() => handleSort('setterName')}
              >
                Setter Name {getSortIndicator('setterName')}
              </th>
              <th
                style={{ ...thStyle, ...thClickableStyle }}
                onClick={() => handleSort('routeCount')}
              >
                Routes Set {getSortIndicator('routeCount')}
              </th>
              <th
                style={{ ...thStyle, ...thClickableStyle }}
                onClick={() => handleSort('averageRating')}
              >
                Avg Rating {getSortIndicator('averageRating')}
              </th>
              <th
                style={{ ...thStyle, ...thClickableStyle }}
                onClick={() => handleSort('totalSends')}
              >
                Total Sends {getSortIndicator('totalSends')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedSetters.map((setter) => (
              <tr key={setter.setterId} style={rowStyle}>
                <td style={tdStyle}>
                  <span style={setterNameStyle}>{setter.setterName}</span>
                </td>
                <td style={tdCenterStyle}>
                  <span style={routeCountStyle}>{setter.routeCount}</span>
                </td>
                <td style={tdCenterStyle}>
                  {setter.averageRating !== null ? (
                    <span style={ratingStyle}>
                      <span style={starStyle}>&#9733;</span>
                      {setter.averageRating.toFixed(1)}
                    </span>
                  ) : (
                    <span style={noRatingStyle}>-</span>
                  )}
                </td>
                <td style={tdCenterStyle}>
                  <span style={sendsStyle}>{setter.totalSends}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary row */}
      <div style={summaryStyle}>
        <span style={summaryItemStyle}>
          <strong>{setters.length}</strong> setters
        </span>
        <span style={summaryItemStyle}>
          <strong>{setters.reduce((sum, s) => sum + s.routeCount, 0)}</strong> total routes
        </span>
        <span style={summaryItemStyle}>
          <strong>{setters.reduce((sum, s) => sum + s.totalSends, 0)}</strong> total sends
        </span>
      </div>
    </div>
  );
};

/**
 * Component styles
 */
const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: '1.125rem',
  fontWeight: 600,
  color: '#1f2937',
};

const subtitleStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.875rem',
  color: '#6b7280',
};

const emptyStateStyle: CSSProperties = {
  padding: '32px',
  textAlign: 'center',
  backgroundColor: '#f9fafb',
  borderRadius: '8px',
  border: '1px dashed #d1d5db',
};

const emptyTextStyle: CSSProperties = {
  margin: '0 0 8px 0',
  fontSize: '0.875rem',
  color: '#374151',
  fontWeight: 500,
};

const emptySubtextStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.8125rem',
  color: '#6b7280',
};

const tableContainerStyle: CSSProperties = {
  overflowX: 'auto',
};

const tableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.875rem',
};

const thStyle: CSSProperties = {
  padding: '12px 16px',
  borderBottom: '2px solid #e5e7eb',
  textAlign: 'center',
  fontWeight: 600,
  color: '#374151',
  whiteSpace: 'nowrap',
};

const thClickableStyle: CSSProperties = {
  cursor: 'pointer',
  userSelect: 'none',
};

const sortIndicatorActiveStyle: CSSProperties = {
  marginLeft: '4px',
  color: '#3b82f6',
  fontSize: '0.75rem',
};

const sortIndicatorInactiveStyle: CSSProperties = {
  marginLeft: '4px',
  color: '#d1d5db',
  fontSize: '0.75rem',
};

const rowStyle: CSSProperties = {
  borderBottom: '1px solid #f3f4f6',
};

const tdStyle: CSSProperties = {
  padding: '14px 16px',
  verticalAlign: 'middle',
};

const tdCenterStyle: CSSProperties = {
  ...tdStyle,
  textAlign: 'center',
};

const setterNameStyle: CSSProperties = {
  fontWeight: 500,
  color: '#1f2937',
};

const routeCountStyle: CSSProperties = {
  display: 'inline-block',
  padding: '4px 12px',
  backgroundColor: '#eff6ff',
  borderRadius: '12px',
  color: '#1d4ed8',
  fontWeight: 600,
};

const ratingStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  color: '#1f2937',
  fontWeight: 500,
};

const starStyle: CSSProperties = {
  color: '#fbbf24',
  fontSize: '0.875rem',
};

const noRatingStyle: CSSProperties = {
  color: '#9ca3af',
};

const sendsStyle: CSSProperties = {
  color: '#059669',
  fontWeight: 600,
};

const summaryStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: '24px',
  padding: '16px',
  backgroundColor: '#f9fafb',
  borderRadius: '8px',
};

const summaryItemStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: '#6b7280',
};

export default SetterPerformanceTable;
